import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import type { TokenUsageStatus } from '@/types/token';

export interface TokenLimitInfo {
  isWithinLimit: boolean;
  hardLimit: number;
  currentUsage: number;
  percentageUsed: number;
  message: string;
}

const fetcher = async (url: string): Promise<TokenUsageStatus> => {
  const response = await backendClient.get(url);
  return response.data as TokenUsageStatus;
};

export const useTokenLimit = () => {
  const { data: tokenUsage, isLoading, error } = useSWR<TokenUsageStatus>(
    '/user/me/token-usage',
    fetcher,
    {
      dedupingInterval: 60000, // Consider data stale after 1 minute
      refreshInterval: 300000, // Refetch every 5 minutes
      revalidateOnFocus: true,
    }
  );

  const tokenLimitInfo: TokenLimitInfo = {
    isWithinLimit: tokenUsage?.is_within_limit ?? true,
    hardLimit: tokenUsage?.hard_limit ?? 100_000_000,
    currentUsage: tokenUsage?.current_month_usage.total_tokens ?? 0,
    percentageUsed: tokenUsage?.percentage_used ?? 0,
    message: tokenUsage?.is_within_limit === false
      ? `You have exceeded your configured limit of ${formatMillions(tokenUsage.hard_limit)} tokens. Please contact support@crimson.law to increase this limit.`
      : '',
  };

  return {
    tokenLimitInfo,
    tokenUsage, // Also return the full data for components that need it
    isLoading,
    error,
  };
};

const formatMillions = (num: number): string => {
  return `${(num / 1_000_000).toFixed(0)}M`;
};